/**
 * Active Panel Module
 * Tracks which panel is currently in focus (highest intersectionRatio ≥ 0.5)
 * Ensures exactly ONE panel has .is-active class at any time
 * Note: Uses IIFE pattern for Ghost's concatenated build system
 */

(function (window) {
  'use strict';

  class ActivePanel {
    constructor(core) {
      this.core = core;
      this.panels = [];
      this.activePanelElement = null;
      this.activeIndex = -1;
      this.panelRatios = new Map(); // Track intersection ratios

      // Navigation guard: suppress observer-driven activations during/after fadeTo
      this._navigationGuard = false;
      this._guardTimeout = null;
      this._fadeToGeneration = 0; // Prevents stale guard timeouts from overlapping fadeTo calls
      this._wheelBlocker = null; // Stored reference for cleanup across fadeTo calls

      this.init();
    }

    init() {
      if (!this.core.root) return;

      // Find all panels WITHOUT disabled markers
      var allPanels = Array.from(
        this.core.root.querySelectorAll('.story-panel')
      );

      // Filter out disabled panels
      this.panels = allPanels.filter(function(panel) {
        return !panel.hasAttribute('data-panel-disabled') &&
               !panel.classList.contains('is-disabled');
      });

      if (this.panels.length === 0) {
        return;
      }

      // Initialize ratio tracking
      var self = this;
      this.panels.forEach(function(panel) {
        self.panelRatios.set(panel, 0);
      });

      // Setup custom IntersectionObserver with multiple thresholds
      this.setupActiveObserver();

      // Register with core
      this.core.registerModule('activePanel', this);

      // Expose global state (will be set when first panel becomes active)
      window.storyActivePanel = null;
      window.storyActiveIndex = -1;

      // Expose Navigation API
      this.exposeAPI();
    }

    exposeAPI() {
      var self = this;

      window.StoryAPI = {
        // Get all active panels (excluding disabled)
        getPanels: function() {
          return self.panels;
        },

        // Get total count of active panels
        getTotal: function() {
          return self.panels.length;
        },

        // Get current active index
        getIndex: function() {
          return self.activeIndex;
        },

        // Get current active panel element
        getActive: function() {
          return self.activePanelElement;
        },

        // Navigate to specific panel by index
        goto: function(index) {
          if (index < 0 || index >= self.panels.length) {
            console.warn('StoryAPI.goto: Index out of range:', index);
            return;
          }

          var targetPanel = self.panels[index];
          if (!targetPanel) return;

          // Check if current panel is panorama with scroll block
          var currentPanoSlide = self.activePanelElement ? self.activePanelElement.querySelector('.pano-slide') : null;
          var wasBlocked = document.body.style.overflow === 'hidden';

          // Temporarily unblock scroll for navigation
          if (wasBlocked && currentPanoSlide) {
            document.body.style.overflow = '';
          }

          // Navigate
          targetPanel.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          // Re-block after scroll animation (~800ms)
          if (wasBlocked && currentPanoSlide) {
            setTimeout(function() {
              // Check if NEW panel is also panorama
              var newPanoSlide = targetPanel.querySelector('.pano-slide');
              if (newPanoSlide) {
                document.body.style.overflow = 'hidden';
              }
              // If new panel is NOT panorama, scroll stays enabled (correct behavior)
            }, 800);
          }
        },

        // Navigate with fade transition (for hub-managed navigation)
        // Fades out → instant scroll → fades in. No intermediate panels visible.
        // Uses inline styles to avoid interfering with data-reveal transitions.
        // CRITICAL: Disconnects IntersectionObserver during entire transition
        // to prevent snap-drift from activating wrong panels.
        fadeTo: function(index, callback) {
          if (index < 0 || index >= self.panels.length) {
            console.warn('StoryAPI.fadeTo: Index out of range:', index);
            if (callback) callback(false);
            return;
          }

          var targetPanel = self.panels[index];
          if (!targetPanel) {
            if (callback) callback(false);
            return;
          }

          // Find the story wrapper for fading
          var wrapper = document.querySelector('.story-scrolly-wrapper');
          if (!wrapper) {
            // Fallback: no wrapper — direct scroll + force-activate
            self.panels[index].scrollIntoView({ behavior: 'instant', block: 'start' });
            self.activatePanel(targetPanel, true);
            if (callback) callback(true);
            return;
          }

          // PHASE 0: Guard + wheel block + observer disconnect
          self._fadeToGeneration++;
          var myGeneration = self._fadeToGeneration;
          if (self._guardTimeout) clearTimeout(self._guardTimeout);
          self._navigationGuard = true;

          // Clean up previous wheel blocker if exists, then install new one
          if (self._wheelBlocker) {
            document.removeEventListener('wheel', self._wheelBlocker, { capture: true });
          }
          self._wheelBlocker = function(e) { e.preventDefault(); };
          document.addEventListener('wheel', self._wheelBlocker, { passive: false, capture: true });

          // Disconnect IntersectionObserver entirely.
          if (self.observer) {
            self.observer.disconnect();
          }

          // Phase 1: Fade out using inline styles (no permanent CSS transition)
          wrapper.style.transition = 'opacity 250ms ease-in-out';
          wrapper.style.pointerEvents = 'none';
          // Force reflow so transition applies before opacity change
          void wrapper.offsetHeight;
          wrapper.style.opacity = '0';

          setTimeout(function() {
            // Phase 2: Set view-gate while invisible — browser can only snap to target view
            if (window.StoryViewGate) {
              window.StoryViewGate.prepareFor(index);
            }
            void wrapper.offsetHeight; // Force layout before scroll

            // Handle panorama scroll block
            var currentPanoSlide = self.activePanelElement ?
              self.activePanelElement.querySelector('.pano-slide') : null;
            var wasBlocked = document.body.style.overflow === 'hidden';
            if (wasBlocked && currentPanoSlide) {
              document.body.style.overflow = '';
            }

            targetPanel.scrollIntoView({
              behavior: 'instant',
              block: 'start'
            });

            // Handle panorama scroll block on target
            if (wasBlocked && currentPanoSlide) {
              var newPanoSlide = targetPanel.querySelector('.pano-slide');
              if (newPanoSlide) {
                document.body.style.overflow = 'hidden';
              }
            }

            // Phase 3: Wait briefly, then fade in
            setTimeout(function() {
              wrapper.style.opacity = '1';

              // Phase 4: After fade-in completes, clean up and reconnect
              setTimeout(function() {
                wrapper.style.transition = '';
                wrapper.style.opacity = '';
                wrapper.style.pointerEvents = '';

                // 4a: Reset ratios — set target to 1.0
                self.panelRatios.clear();
                self.panelRatios.set(targetPanel, 1.0);

                // 4b: Force-activate target (bypasses guard)
                self.activatePanel(targetPanel, true);

                // 4c: Reconnect observer (fires async, but guard blocks activation)
                if (self.observer) {
                  self.panels.forEach(function(panel) {
                    self.observer.observe(panel);
                  });
                }

                // 4d: Drop guard after delay. With view-gating, snap-drift
                // is structurally impossible, so a simple timer is safe.
                self._guardTimeout = setTimeout(function() {
                  if (self._fadeToGeneration !== myGeneration) return;
                  self._dropGuard();
                }, 300);

                // 4e: Failsafe — guarantee guard drops even if generation check
                // causes the normal timeout to skip (e.g. overlapping fadeTo calls).
                setTimeout(function() {
                  if (self._navigationGuard || self._wheelBlocker) {
                    self._dropGuard();
                  }
                }, 2000);

                if (callback) callback(true);
              }, 280);
            }, 300);
          }, 280);
        },

        // Navigate to next panel
        next: function() {
          var nextIndex = self.activeIndex + 1;
          if (nextIndex < self.panels.length) {
            this.goto(nextIndex);
          }
        },

        // Navigate to previous panel
        prev: function() {
          var prevIndex = self.activeIndex - 1;
          if (prevIndex >= 0) {
            this.goto(prevIndex);
          }
        },

        // Optional: Register callback for panel changes
        onChange: function(callback) {
          if (typeof callback === 'function') {
            document.addEventListener('story:panel:activated', function(e) {
              callback(e.detail);
            });
          }
        }
      };
    }

    _dropGuard() {
      if (this._wheelBlocker) {
        document.removeEventListener('wheel', this._wheelBlocker, { capture: true });
        this._wheelBlocker = null;
      }
      this._navigationGuard = false;
    }

    setupActiveObserver() {
      var self = this;

      // Observer with fine-grained thresholds for accurate tracking
      this.observer = new IntersectionObserver(
        function(entries) {
          self.handleIntersection(entries);
        },
        {
          threshold: [0, 0.25, 0.5, 0.75, 1],
          rootMargin: '0px'
        }
      );

      // Observe all active panels
      this.panels.forEach(function(panel) {
        self.observer.observe(panel);
      });
    }

    handleIntersection(entries) {
      var self = this;

      // Always update intersection ratios (even during guard).
      // Ratio is max(panel-relative, viewport-relative). Short panels (<=100vh)
      // use the panel-relative value (a fully visible 50vh panel scores 1.0).
      // Long panels (e.g. 300vh sticky scenes) would never reach the 0.5
      // threshold panel-relatively (max = viewport/panelHeight = 0.33), so we
      // also measure how much of the viewport they fill — a sticky scene that
      // fully covers the viewport scores 1.0 that way.
      entries.forEach(function(entry) {
        var rect = entry.boundingClientRect;
        var viewportVisible = Math.min(rect.bottom, window.innerHeight) -
                              Math.max(rect.top, 0);
        var viewportRatio = Math.max(0, viewportVisible) / window.innerHeight;
        var ratio = Math.max(entry.intersectionRatio, viewportRatio);
        self.panelRatios.set(entry.target, ratio);
      });

      // Guard: skip activation during navigation
      if (self._navigationGuard) return;

      // Find panel with highest ratio ≥ 0.5
      var bestPanel = null;
      var bestRatio = 0.5; // Minimum threshold

      self.panels.forEach(function(panel) {
        var ratio = self.panelRatios.get(panel) || 0;
        if (ratio >= bestRatio) {
          bestRatio = ratio;
          bestPanel = panel;
        }
      });

      // If no panel meets threshold, keep last active panel
      if (!bestPanel && self.activePanelElement) {
        // Don't change - last active panel stays active
        return;
      }

      // If we found a new best panel, activate it
      if (bestPanel && bestPanel !== self.activePanelElement) {
        self.activatePanel(bestPanel);
      }
    }

    activatePanel(panel, force) {
      // Guard: block observer-driven activations, allow force-activations
      if (this._navigationGuard && !force) return;

      var previousPanel = this.activePanelElement;
      var previousIndex = this.activeIndex;

      // Dispatch deactivated event for previous panel (if exists)
      if (previousPanel) {
        previousPanel.classList.remove('is-active');

        var deactivatedEvent = new CustomEvent('story:panel:deactivated', {
          detail: {
            panel: previousPanel,
            index: previousIndex
          },
          bubbles: true
        });
        previousPanel.dispatchEvent(deactivatedEvent);
      }

      // Add .is-active to new panel
      panel.classList.add('is-active');

      // Update internal state
      this.activePanelElement = panel;
      this.activeIndex = this.panels.indexOf(panel);

      // Update global state
      window.storyActivePanel = panel;
      window.storyActiveIndex = this.activeIndex;

      // Dispatch activated event for new panel
      var activatedEvent = new CustomEvent('story:panel:activated', {
        detail: {
          panel: panel,
          index: this.activeIndex,
          total: this.panels.length
        },
        bubbles: true
      });
      panel.dispatchEvent(activatedEvent);

      // Debug logging (optional, remove in production)
      if (window.location.search.indexOf('debug=story') !== -1) {
        console.log('Active Panel Changed:', {
          index: this.activeIndex,
          total: this.panels.length,
          element: panel
        });
      }
    }

    getMostVisiblePanel() {
      var best = null;
      var bestRatio = 0.5;
      var self = this;
      this.panels.forEach(function(p) {
        var r = self.panelRatios.get(p) || 0;
        if (r >= bestRatio) { bestRatio = r; best = p; }
      });
      return best;
    }

    destroy() {
      if (this._guardTimeout) clearTimeout(this._guardTimeout);
      if (this._wheelBlocker) {
        document.removeEventListener('wheel', this._wheelBlocker, { capture: true });
        this._wheelBlocker = null;
      }
      this._navigationGuard = false;
      var self = this;
      if (this.observer) {
        this.panels.forEach(function(panel) {
          self.observer.unobserve(panel);
        });
        this.observer.disconnect();
      }
      this.panels = [];
      this.panelRatios.clear();
      this.activePanelElement = null;
      this.activeIndex = -1;
    }
  }

  // Expose ActivePanel to window for other modules
  window.StoryModuleActivePanel = ActivePanel;

})(window);
